import { DocSection, Callout, kbd } from '../../../pages/shell';
import { DocShot } from '../../DocShot';
import { Link } from 'react-router-dom';

export const toc = [
  { id: 'overview', label: 'Overview' },
  { id: 'three-passes', label: 'The three passes' },
  { id: 'scenes-shots', label: 'Scenes & shots' },
  { id: 'gpu', label: 'Analysis runs on your GPU' },
  { id: 'where-data-lives', label: 'Where analysis data lives' },
  { id: 'content-linking', label: 'Files linked by content, not path' },
  { id: 're-index', label: 'Re-index for a new model' },
];

export default function Body() {
  return (
    <>
      <DocSection id="overview" title="Overview">
        <p>
          Before ClipRadar can search a piece of footage, it needs to analyze it once. Add a file or
          folder on the <span style={kbd}>Media</span> tab, and each row shows three cells —{' '}
          <span style={kbd}>VISUAL</span>, <span style={kbd}>SPEECH</span>, <span style={kbd}>FACES</span> —
          tracking whether that source is done, queued, or still eligible to run.
        </p>
        <DocShot
          slug="analyzing-media"
          shot="media-cells"
          alt="Media table with Visual/Speech/Faces cells in done, queued and eligible states"
          caption="A green check means a pass is done; a dashed box means it's eligible — click it to queue that pass."
        />
      </DocSection>
      <DocSection id="three-passes" title="The three passes">
        <p>
          Analysis runs as three independent passes, all on your GPU: <strong>Visual</strong> (scene
          detection plus a visual embedding of every frame), <strong>Speech</strong> (a full transcript),
          and <strong>Faces</strong> (face detection for the People board). You don't have to run all
          three on every source — queue only what you need, or use{' '}
          <span style={kbd}>Analyze All (visual + speech + faces)</span> from a row's right-click menu to
          run everything at once.
        </p>
        <p>
          Each pass feeds a different way of searching: Visual powers{' '}
          <Link to="/docs/concepts/visual-search">visual search</Link>, Speech powers{' '}
          <Link to="/docs/concepts/speech-search">speech search</Link>, and Faces powers{' '}
          <Link to="/docs/concepts/people-and-faces">people &amp; faces</Link>. A source only shows up in
          the kind of search it's been analyzed for.
        </p>
      </DocSection>
      <DocSection id="scenes-shots" title="Scenes & shots">
        <p>
          The Visual pass doesn't just embed frames — it detects shot cuts and merges them into semantic{' '}
          <strong>scenes</strong>. That's why a search result is one card per scene, not one per raw shot
          cut and not one per whole file: a five-minute interview with a dozen cutaways returns a dozen
          distinct, timestamped moments instead of one giant clip or a flood of near-duplicate frames.
        </p>
      </DocSection>
      <DocSection id="gpu" title="Analysis runs on your GPU">
        <p>
          Scene detection, visual embeddings, speech transcription, and face recognition all run on your
          GPU. An <strong>NVIDIA GPU (CUDA) is required</strong> — there's no supported way to analyze
          footage without one. See{' '}
          <Link to="/docs/concepts/models-and-your-gpu">AI models &amp; your GPU</Link> for how ClipRadar
          adapts its batch sizes to the VRAM you actually have free, and what changes on a weaker card. You
          can keep working while analysis runs, and queue more sources mid-run.
        </p>
      </DocSection>
      <DocSection id="where-data-lives" title="Where analysis data lives">
        <p>
          Analysis results — thumbnails, embeddings, transcripts, face data — are written to ClipRadar's{' '}
          <strong>Cache folder</strong>, one folder per source. Your original footage is never touched,
          moved, or re-encoded to produce this; ClipRadar only ever reads it. Progress shows as a two-bar
          indicator at the bottom of the Media tab while a batch is running.
        </p>
        <DocShot slug="analyzing-media" shot="progress" alt="Two-bar analysis progress" caption="Analysis progress for a batch of queued sources." />
        <p>
          For the exact folder paths (and how to move or reset the Cache folder), see{' '}
          <Link to="/docs/reference/where-your-data-lives">Where ClipRadar stores things</Link>.
        </p>
      </DocSection>
      <DocSection id="content-linking" title="Files linked by content, not path">
        <p>
          ClipRadar identifies an analyzed source by its content, not by where it happens to sit on disk.
          Renaming a file, or moving it within a folder ClipRadar already knows about, doesn't throw away
          its analysis — the row keeps its Visual, Speech, and Faces state.
        </p>
        <p>
          If a file becomes genuinely unreachable — moved out of any imported folder, or its drive isn't
          connected — the row shows it as missing. <span style={kbd}>Relink missing file…</span> from the
          row's menu points ClipRadar at the file's new location without re-running analysis.
        </p>
      </DocSection>
      <DocSection id="re-index" title="Re-index for a new model">
        <p>
          Occasionally a ClipRadar update ships a newer visual model. Sources analyzed under the older one
          are flagged as needing re-index, and a banner on the Media tab points you to them.
        </p>
        <Callout kind="warn">
          Queue <span style={kbd}>Re-index for New Model</span> from a flagged row's menu (or the banner)
          to re-embed it under the current model — it's a normal maintenance step, not a sign anything
          went wrong.
        </Callout>
      </DocSection>
    </>
  );
}
